<template>
  <div class="app-container" :class="{ 'loading': isLoading }">
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>数据加载中...</p>
    </div>

    <TechBackground />

    <div class="main-content">
      <header class="header-section">
        <div class="header-left">
          <span class="company-name">{{ headerLeft || companyName }}</span>
        </div>
        <div class="header-center">
          <h1 class="dashboard-title">{{ screenTitle }}</h1>
          <div class="update-info">
            <svg class="time-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span>最后更新：{{ lastUpdateTime }}</span>
          </div>
        </div>
        <div class="header-right">
          <span class="support-info">{{ headerRight || '技术支持：飞米科技' }}</span>
        </div>
      </header>

      <div v-if="style === 'style3'" class="content-style3">
        <div class="style3-top" :style="{ width: topWidth + '%' }">
          <div class="style3-top-grid">
            <div
              v-for="(section, idx) in panels.top"
              :key="'top-' + idx"
              class="panel-section style3-top-panel"
            >
              <component
                :is="componentMap[section.type]"
                :ref="el => setSectionRef('top', idx, el)"
                v-bind="getSectionProps(section)"
              />
            </div>
          </div>
        </div>

        <div class="style3-bottom">
          <div class="style3-viewport">
            <div ref="regionTrack" class="style3-track">
              <div
                v-for="(section, idx) in regionItems"
                :key="'bottom-' + idx"
                class="style3-region"
              >
                <component
                  :is="componentMap[section.type]"
                  :ref="el => setSectionRef('bottom', idx, el)"
                  v-bind="getSectionProps(section)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="content-grid" :class="'layout-' + style" :style="gridStyle">
        <div
          v-for="panelKey in activePanelKeys"
          :key="panelKey"
          :class="['panel', panelKey + '-panel']"
        >
          <div
            v-for="(section, idx) in panels[panelKey]"
            :key="panelKey + '-' + idx"
            class="panel-section"
            :style="{ height: section.height + '%' }"
          >
            <component
              :is="componentMap[section.type]"
              :ref="el => setSectionRef(panelKey, idx, el)"
              v-bind="getSectionProps(section)"
            />
          </div>
        </div>
      </div>
    </div>

    <transition name="fade">
      <div v-if="showUpdateTip" class="update-tip">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="23 4 23 10 17 16 7 16 1 10 1 4"></polyline>
        </svg>
        <span>数据已更新</span>
      </div>
    </transition>
  </div>
</template>

<script>
import TechBackground from '../components/TechBackground.vue'
import HealthCertificates from '../components/HealthCertificates.vue'
import DataTable from '../components/DataTable.vue'
import MorningCheck from '../components/MorningCheck.vue'
import SampleRecord from '../components/SampleRecord.vue'
import DishTemperature from '../components/DishTemperature.vue'
import VideoEventCarousel from '../components/VideoEventCarousel.vue'
import VideoAlarmOverview from '../components/VideoAlarmOverview.vue'
import TwoColImages from '../components/TwoColImages.vue'
import VideoMonitor from '../components/VideoMonitor.vue'
import FoodSafetyInspection from '../components/FoodSafetyInspection.vue'
import ImageGrid from '../components/ImageGrid.vue'
import MultiLineChart from '../components/MultiLineChart.vue'
import SingleImage from '../components/SingleImage.vue'
import EventScrollList from '../components/EventScrollList.vue'
import { fetchScreenData } from '../utils/api.js'

export default {
  name: 'DailyRecord',
  components: {
    TechBackground,
    HealthCertificates,
    DataTable,
    MorningCheck,
    SampleRecord,
    DishTemperature,
    VideoEventCarousel,
    VideoAlarmOverview,
    TwoColImages,
    VideoMonitor,
    FoodSafetyInspection,
    ImageGrid,
    MultiLineChart,
    SingleImage,
    EventScrollList
  },
  data() {
    return {
      companyName: '食品安全监控中心',
      screenTitle: '食堂智慧食安驾驶舱',
      headerLeft: '',
      headerRight: '',
      lastUpdateTime: '',
      isLoading: true,
      showUpdateTip: false,

      style: 'style1',
      topWidth: 100,
      leftWidth: 50,
      scrollInterval: 5,
      refreshInterval: 10,
      regionIndex: 0,
      regionTimer: null,
      regionResetTimer: null,
      panelKeys: ['left', 'center', 'right'],
      panels: {
        left: [],
        center: [],
        right: [],
        top: [],
        bottom: []
      },

      componentMap: {
        TwoColImages,
        HealthCertificates,
        DataTable,
        MorningCheck,
        SampleRecord,
        DishTemperature,
        VideoEventCarousel,
        VideoAlarmOverview,
        VideoMonitor,
        FoodSafetyInspection,
        ImageGrid,
        MultiLineChart,
        SingleImage,
        EventScrollList
      },

      sectionRefMap: {},

      updateTimer: null
    }
  },
  computed: {
    activePanelKeys() {
      return this.style === 'style2' ? ['left', 'right'] : this.panelKeys
    },
    gridStyle() {
      if (this.style !== 'style2') return {}
      const left = Math.min(Math.max(Number(this.leftWidth) || 50, 10), 90)
      return { gridTemplateColumns: `${left}% ${100 - left}%` }
    },
    regionItems() {
      const list = this.panels.bottom || []
      return list.length > 3 ? [...list, ...list] : list
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.handleResize)
  },
  watch: {
    '$route.query': {
      handler() {
        this.loadData()
      }
    }
  },
  beforeDestroy() {
    if (this.updateTimer) {
      clearInterval(this.updateTimer)
    }
    this.stopRegionScroll()
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async loadData(showLoading = true) {
      if (showLoading) {
        this.isLoading = true
      }
      this.stopRegionScroll()
      this.regionIndex = 0

      try {
        const data = await fetchScreenData()

        this.$nextTick(() => {
          this.companyName = data.companyName || '食品安全监控中心'
          this.screenTitle = data.screenTitle || '食堂智慧食安驾驶舱'
          this.headerLeft = data.headerLeft || ''
          this.headerRight = data.headerRight || ''
          this.style = data.style || 'style1'
          this.topWidth = Number(data.top_width) || 100
          this.leftWidth = Number(data.left_width) || 50
          this.scrollInterval = Number(data.scroll_interval) || 5
          this.refreshInterval = (data.refresh_interval === undefined || data.refresh_interval === null)
            ? 10
            : (Number(data.refresh_interval) || 0)

          if (data.panels) {
            this.panels = {
              left: data.panels.left || [],
              center: data.panels.center || [],
              right: data.panels.right || [],
              top: data.panels.top || [],
              bottom: data.panels.bottom || []
            }
          }

          this.lastUpdateTime = new Date().toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })

          this.isLoading = false
          this.startAutoUpdate()

          setTimeout(() => {
            this.startScrolls()
            this.startRegionScroll()
          }, 800)
        })
      } catch (error) {
        console.error('数据加载失败:', error)
        this.isLoading = false
      }
    },

    setSectionRef(panelKey, idx, el) {
      if (el) {
        const key = panelKey + '-' + idx
        this.$set(this.sectionRefMap, key, el)
      }
    },

    getSectionProps(section) {
      const { type, title, data } = section
      switch (type) {
        case 'TwoColImages':
          return {
            title: title || '',
            col1: (data && data.col1) || [],
            col2: (data && data.col2) || []
          }
        case 'MorningCheck':
          return {
            title: title || '',
            mode: section.display_mode || 'table',
            headers: (data && data.headers) || [],
            records: (data && data.records) || [],
            stats: (data && data.stats) || [],
            overview: (data && data.overview) || {}
          }
        case 'SampleRecord':
          return {
            title: title || '',
            mode: section.display_mode || 'table',
            headers: (data && data.headers) || [],
            records: (data && data.records) || [],
            groups: (data && data.groups) || [],
            stats: (data && data.stats) || []
          }
        case 'DishTemperature':
          return {
            title: title || '',
            groups: (data && data.groups) || [],
            stats: (data && data.stats) || []
          }
        case 'VideoEventCarousel':
          return {
            title: title || '',
            events: Array.isArray(data) ? data : []
          }
        case 'VideoAlarmOverview':
          return {
            title: title || '',
            overview: (data && typeof data === 'object') ? data : {}
          }
        case 'HealthCertificates':
          return {
            title: title || '',
            certificates: (data && data) || []
          }
        case 'DataTable':
          return {
            title: title || '',
            headers: (data && data.headers) || [],
            records: (data && data.records) || [],
            stats: (data && data.stats) || []
          }
        case 'VideoMonitor':
          return {
            title: title || '',
            cameras: (data && data) || []
          }
        case 'FoodSafetyInspection':
          return {
            inspection: (data && data.inspection) || {},
            trainingDetail: (data && data.trainingDetail) || []
          }
        case 'ImageGrid':
          return {
            title: title || '',
            images: (data && data.images) || (data && data) || []
          }
        case 'MultiLineChart':
          return {
            title: title || '',
            series: Array.isArray(data) ? data : []
          }
        case 'SingleImage':
          return {
            title: title || '',
            images: (data && data.images) || []
          }
        case 'EventScrollList':
          if (data && !Array.isArray(data) && data.normal) {
            return {
              title: title || '',
              records: [],
              normal: data
            }
          }
          return {
            title: title || '',
            records: Array.isArray(data) ? data : [],
            normal: null
          }
        default:
          return {}
      }
    },

    getSectionFlex(panelKey) {
      const count = this.panels[panelKey].length || 1
      return count === 1 ? '1 1 100%' : '1 1 0'
    },

    startScrolls() {
      Object.values(this.sectionRefMap).forEach(comp => {
        if (comp && comp.startAutoScroll) {
          comp.startAutoScroll()
        }
      })
    },

    handleResize() {
      if (this.style === 'style3') {
        this.applyRegionTransform(false)
      }
    },

    startRegionScroll() {
      this.stopRegionScroll()
      const total = (this.panels.bottom || []).length
      if (this.style !== 'style3' || total <= 3) {
        this.applyRegionTransform(false)
        return
      }

      this.applyRegionTransform(false)
      const interval = Math.max(Number(this.scrollInterval) || 5, 1) * 1000
      this.regionTimer = setInterval(() => {
        this.regionIndex += 1
        this.applyRegionTransform(true)
        if (this.regionIndex >= total) {
          this.regionResetTimer = setTimeout(() => {
            this.regionIndex = 0
            this.applyRegionTransform(false)
          }, 650)
        }
      }, interval)
    },

    stopRegionScroll() {
      if (this.regionTimer) {
        clearInterval(this.regionTimer)
        this.regionTimer = null
      }
      if (this.regionResetTimer) {
        clearTimeout(this.regionResetTimer)
        this.regionResetTimer = null
      }
    },

    applyRegionTransform(animate) {
      const track = this.$refs.regionTrack
      if (!track) return
      const first = track.children[0]
      if (!first) return
      const styles = window.getComputedStyle(track)
      const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0
      const step = first.getBoundingClientRect().width + gap
      track.style.transition = animate
        ? 'transform 0.6s cubic-bezier(0.22, 0.61, 0.36, 1)'
        : 'none'
      track.style.transform = `translateX(-${this.regionIndex * step}px)`
    },

    startAutoUpdate() {
      if (this.updateTimer) {
        clearInterval(this.updateTimer)
        this.updateTimer = null
      }
      const interval = Number(this.refreshInterval) || 0
      if (interval <= 0) return
      this.updateTimer = setInterval(() => {
        this.loadData(false)
        this.showUpdateTip = true
        setTimeout(() => {
          this.showUpdateTip = false
        }, 3000)
      }, interval * 60 * 1000)
    }
  }
}
</script>

<style scoped>
.app-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.main-content {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  padding: 10px;
  padding-top: 15px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.header-section {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0px 0 15px 0;
  animation: slideDown 0.8s ease-out;
  flex-shrink: 0;
  background: url('../assets/header.png') center/cover no-repeat;
  border-radius: 8px;
}

.header-left {
  flex: 0 0 200px;
  display: flex;
  align-items: center;
}

.company-name {
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  text-shadow: 0 0 10px rgba(99, 179, 237, 0.5);
  letter-spacing: 2px;
}

.header-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dashboard-title {
  font-size: 22px;
  font-weight: 700;
  color: #e2e8f0;
  text-shadow: 0 0 20px rgba(99, 179, 237, 0.5);
  margin: 0 0 2px 0;
  letter-spacing: 4px;
}

.update-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  color: #a0aec0;
}

.time-icon {
  width: 14px;
  height: 14px;
  color: #63b3ed;
}

.header-right {
  flex: 0 0 200px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.support-info {
  font-size: 14px;
  letter-spacing: 1px;
}

.content-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  height: calc(100vh - 100px);
  max-width: 100%;
}

.panel {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100vh - 100px);
}

/* 样式三：上下分区 + 下半区区域轮播 */
.content-style3 {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: calc(100vh - 100px);
  max-width: 100%;
}

.style3-top {
  flex: 1;
  min-height: 0;
  align-self: center;
  transition: width 0.35s ease;
}

.style3-top-grid {
  height: 100%;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 16px;
}

.style3-top-panel {
  min-width: 0;
  min-height: 0;
  height: 100%;
}

.style3-bottom {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.style3-viewport {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.style3-track {
  display: flex;
  gap: 16px;
  height: 100%;
  will-change: transform;
}

.style3-region {
  flex: 0 0 calc((100% - 32px) / 3);
  height: 100%;
  min-width: 0;
}

.style3-region > * {
  height: 100%;
}

.left-panel {
  animation: slideLeft 0.8s ease-out 0.2s both;
}

.center-panel {
  animation: slideUp 0.8s ease-out 0.3s both;
}

.right-panel {
  animation: slideRight 0.8s ease-out 0.4s both;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(10, 20, 40, 0.9);
  z-index: 100;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba(99, 179, 237, 0.3);
  border-top-color: #63b3ed;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-overlay p {
  color: #a0aec0;
  margin-top: 16px;
  font-size: 14px;
}

.update-tip {
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(72, 187, 120, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 1000;
}

.update-tip svg {
  width: 18px;
  height: 18px;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
